import React, { useState } from 'react'
import axios from 'axios'
import Modal from '../../shared/modal'
import { admin_robot_sensors_path, admin_robot_sensor_path, ping_admin_robot_sensors_path } from '../../../../routes'
import { LocalizationContextType } from '../../shared/contexts'
import { formatTemperature } from '../../../utils/formatTemperature'

export interface SensorFormValues {
  id: number
  sensor_id: string
  sensor_name: string
  sensor_type: string
  robot_node_id: number | null
}

// The value is the enum key the API expects; the label is what a person reads.
export interface SensorTypeOption {
  value: string
  label: string
}

interface SensorFormModalProps {
  sensorTypes: SensorTypeOption[]
  // Absent when adding a sensor; present when editing an existing one.
  sensor?: SensorFormValues
  localizationContext?: LocalizationContextType
  onClose: () => void
  onSaved: () => void
}

interface PingResult {
  success: boolean
  recorded_value?: number
  error?: string
}

const SensorFormModal: React.FC<SensorFormModalProps> = ({
  sensorTypes,
  sensor,
  localizationContext,
  onClose,
  onSaved
}) => {
  const isEdit = sensor !== undefined

  const [sensorName, setSensorName] = useState(sensor?.sensor_name || '')
  const [sensorId, setSensorId] = useState(sensor?.sensor_id || '')
  const [sensorType, setSensorType] = useState(sensor?.sensor_type || sensorTypes[0]?.value || '')
  const [robotNodeId, setRobotNodeId] = useState(
    sensor?.robot_node_id !== null && sensor?.robot_node_id !== undefined ? String(sensor.robot_node_id) : ''
  )
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pinging, setPinging] = useState(false)
  const [pingResult, setPingResult] = useState<PingResult | null>(null)

  // Reads the typed node id off the sensor server, so a wrong id is caught here
  // rather than by silently missing readings later.
  const pingSensor = () => {
    if (robotNodeId.trim() === '') {
      setPingResult({ success: false, error: 'Enter an OPC-UA node ID first.' })
      return
    }

    setPinging(true)
    setPingResult(null)
    axios
      .post(ping_admin_robot_sensors_path(), { robot_node_id: robotNodeId.trim() })
      .then(({ data }) => setPingResult(data))
      .catch((err) =>
        setPingResult({ success: false, error: err.response?.data?.error || 'Could not reach the sensor.' })
      )
      .finally(() => setPinging(false))
  }

  const pingMessage = () => {
    if (pingResult === null) return null

    if (pingResult.success && pingResult.recorded_value !== undefined) {
      return `Sensor responded: ${formatTemperature(pingResult.recorded_value, localizationContext)}`
    }

    return pingResult.error || 'The sensor did not respond.'
  }

  const submit = () => {
    if (sensorName.trim() === '') {
      setError('Sensor name is required.')
      return
    }
    if (sensorId.trim() === '') {
      setError('Sensor ID is required.')
      return
    }
    if (robotNodeId.trim() === '') {
      setError('OPC-UA node ID is required.')
      return
    }

    const payload = {
      sensor_name: sensorName.trim(),
      sensor_id: sensorId.trim(),
      sensor_type: sensorType,
      robot_node_id: robotNodeId.trim()
    }

    setSubmitting(true)
    setError(null)

    const request = isEdit
      ? axios.put(admin_robot_sensor_path(sensor.id), payload)
      : axios.post(admin_robot_sensors_path(), payload)

    request
      .then(() => onSaved())
      .catch((err) => {
        setError(err.response?.data?.error || `Could not ${isEdit ? 'update' : 'create'} the sensor.`)
        setSubmitting(false)
      })
  }

  const submitLabel = () => {
    if (submitting) return isEdit ? 'Saving…' : 'Creating…'
    return isEdit ? 'Save sensor' : 'Create sensor'
  }

  const footer = (
    <>
      <button
        type="button"
        className="btn btn-secondary"
        onClick={onClose}
        disabled={submitting}
      >
        Cancel
      </button>
      <button
        type="button"
        className="btn btn-primary"
        onClick={submit}
        disabled={submitting}
      >
        {submitLabel()}
      </button>
    </>
  )

  return (
    <Modal
      headerText={isEdit ? 'Edit sensor' : 'Add sensor'}
      onClose={onClose}
      footerContent={footer}
      centered
    >
      {error && <div className="alert alert-danger">{error}</div>}

      <div className="form-group mb-3">
        <label htmlFor="sensor-name">Sensor name</label>
        <input
          id="sensor-name"
          type="text"
          className="form-control"
          value={sensorName}
          onChange={(e) => setSensorName(e.target.value)}
          placeholder="e.g. Fridge Sensor"
        />
      </div>

      <div className="form-group mb-3">
        <label htmlFor="sensor-id">Sensor ID</label>
        <input
          id="sensor-id"
          type="text"
          className="form-control"
          value={sensorId}
          onChange={(e) => setSensorId(e.target.value)}
          placeholder="e.g. F4_1E_57_4D_49_B6"
        />
        <small className="form-text text-muted">
          {isEdit
            ? 'Readings are stored against this value — changing it detaches the readings already recorded under the old id.'
            : 'The beacon’s MAC address, underscore separated. Sensor readings are stored against this value.'}
        </small>
      </div>

      <div className="form-group mb-3">
        <label htmlFor="sensor-type">Sensor type</label>
        <select
          id="sensor-type"
          className="form-select"
          value={sensorType}
          onChange={(e) => setSensorType(e.target.value)}
        >
          {sensorTypes.map(({ value, label }) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </div>

      <div className="form-group mb-3">
        {/* The ping belongs to this field, so it lives on its label row rather than
            trailing the form or dissolving into the input's chrome. */}
        <div className="sensor-node-id-label-row">
          <label htmlFor="sensor-node-id">OPC-UA node ID</label>
          <button
            type="button"
            className="sensor-ping-button"
            onClick={pingSensor}
            disabled={pinging || submitting}
          >
            <i className="bi bi-broadcast-pin me-1" />
            {pinging ? 'Pinging…' : 'Ping to verify'}
          </button>
        </div>

        <input
          id="sensor-node-id"
          type="number"
          min="0"
          step="1"
          className="form-control"
          value={robotNodeId}
          onChange={(e) => setRobotNodeId(e.target.value)}
          placeholder="e.g. 9"
        />

        <small className="form-text text-muted d-block">
          Numeric node id in namespace 2, as assigned by the sensor OPC-UA server. Read it off the server — it is not sequential.
        </small>

        {pingResult && (
          <small className={`sensor-ping-result ${pingResult.success ? 'sensor-ping-result-ok' : 'sensor-ping-result-error'}`}>
            <i className={`bi ${pingResult.success ? 'bi-check-circle-fill' : 'bi-exclamation-circle-fill'} me-1`} />
            {pingMessage()}
          </small>
        )}
      </div>
    </Modal>
  )
}

export default SensorFormModal
